import { useEffect, useRef } from 'react'
import type { LoadPlayPayload } from '@/types/Animation.types'

type UsePlayLoaderParams = {
	/** Current play timing data */
	playTiming: LoadPlayPayload | null
	/** Current play ID (used to detect changes) */
	playId: string | null
	/** Callback to stop current animation */
	stop: () => void
	/** Callback to load new play */
	loadPlay: (timing: LoadPlayPayload) => void
}

/**
 * Handles loading a new play into the animation system when the play changes.
 * Stops the current animation and loads the new one when a different play is selected.
 *
 * This pattern prevents re-loading the same play unnecessarily while ensuring
 * the animation resets when switching between plays.
 *
 * **Relationship with usePlayAnimation:**
 * - usePlayLoader: Low-level utility for the loading pattern (used with any animation approach)
 * - usePlayAnimation: Higher-level hook that combines usePlayLoader with AnimationContext
 *
 * Use usePlayLoader when you need fine-grained control over loading behavior.
 * Use usePlayAnimation when working with AnimationContext (simpler API).
 *
 * @param params - Configuration object
 *
 * @example
 * usePlayLoader({
 *   playTiming,
 *   playId: playContent?.id ?? null,
 *   stop,
 *   loadPlay,
 * })
 */
export function usePlayLoader({
								  playTiming,
								  playId,
								  stop,
								  loadPlay,
							  }: UsePlayLoaderParams): void {
	const previousPlayIdRef = useRef<string | null>(null)

	useEffect(() => {
		if (playTiming && playId !== previousPlayIdRef.current) {
			previousPlayIdRef.current = playId
			stop()
			loadPlay(playTiming)
		}
	}, [playTiming, playId, loadPlay, stop])
}
